import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BlackTechStreetData, latest, pctChange, previous } from '../types';
import { buildCountYAxisScaleForChart } from '../../chartUtils';
import { CHART_GOAL_LINE_COLOR, CHART_GOAL_LINE_PROPS } from '../../constants';
import { countYAxisReferenceGridLines } from '../../countYAxisReferenceGridLines';
import { getMultiGoalStatus, getRangeGoalState, isQuarterInPaceWindow, resolveActiveGoal, targetRange } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function ParticipantEducationCard({ data }: { data: BlackTechStreetData }) {
  const theme = useMantineTheme();
  const series = data.educationOutcomes;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur?.quarter ?? '');

  // QoQ calculations for all metrics
  const certQoq = cur ? pctChange(cur.certifications, prev?.certifications ?? null) : null;
  const aiFluencyQoq = cur ? pctChange(cur.aiFluency, prev?.aiFluency ?? null) : null;
  const digitalLiteracyQoq = cur ? pctChange(cur.digitalLiteracy, prev?.digitalLiteracy ?? null) : null;
  const digitalExposureQoq = cur ? pctChange(cur.digitalExposure, prev?.digitalExposure ?? null) : null;

  const qoqArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqLabel = (n: number | null): string => (n === null ? '— QoQ' : `${qoqArrow(n)}${Math.abs(n).toFixed(2)}% QoQ`);

  // YTD and YoY calculations for all metrics
  const calculateYtd = (dataKey: 'certifications' | 'aiFluency' | 'digitalLiteracy' | 'digitalExposure') => {
    if (!currentMeta) return 0;
    return series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point[dataKey], 0);
  };

  const calculateYoy = (ytd: number, dataKey: 'certifications' | 'aiFluency' | 'digitalLiteracy' | 'digitalExposure') => {
    if (!currentMeta) return null;
    const prevYearTotal = series
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point[dataKey], 0);
    return pctChange(ytd, prevYearTotal);
  };

  const certYtd = calculateYtd('certifications');
  const certYoy = calculateYoy(certYtd, 'certifications');

  const yoyLabel = (n: number | null): string => (n === null ? '— YoY' : `${qoqArrow(n)}${Math.abs(n).toFixed(2)}% YoY`);

  // All-Time totals
  const certAllTime = series.reduce((sum, point) => sum + point.certifications, 0);

  const combinedThisQuarter = (cur?.certifications ?? 0) + (cur?.aiFluency ?? 0);
  const educationGoal = resolveActiveGoal(data.goalsByMetric['gace.educationOutcomes'], { grain: 'quarterly_rate' });
  const combinedRange = targetRange(educationGoal, 'aiFluentOrCertified');
  const inPaceWindow = cur ? isQuarterInPaceWindow(cur.quarter, educationGoal?.paceWindow) : false;
  const status =
    combinedRange != null && inPaceWindow
      ? getMultiGoalStatus([getRangeGoalState({ value: combinedThisQuarter, min: combinedRange.min, max: combinedRange.max })])
      : { label: 'Snapshot' as const, color: 'gray' as const };
  const chartData = series.map((point) => ({
    ...point,
    combined: point.certifications + point.aiFluency,
  }));
  const showGoalLine = combinedRange != null;
  const chartGoalValues = showGoalLine ? [combinedRange.min] : [];
  const { yAxisMax, yAxisTicks } = buildCountYAxisScaleForChart(
    chartData.map((point) => point.combined),
    chartGoalValues
  );
  const gridLineStroke =
    theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)';

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell
      title="ASPIRE Outcomes"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        {/* Industry Certifications - Primary metric with full detail */}
        <MetricRow
          items={[
            {
              label: 'Industry Certifications (This Quarter)',
              labelTooltip: 'Industry recognized certifications achieved this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.certifications ?? '—'}
                </Text>
              ),
              sublabel: qoqLabel(certQoq),
            },
            {
              label: 'Industry Certifications (YTD)',
              labelTooltip: 'Calendar year to date certifications (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {certYtd}
                </Text>
              ),
              sublabel: yoyLabel(certYoy),
            },
            {
              label: 'Industry Certifications (All-Time)',
              labelTooltip: 'Total certifications across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {certAllTime}
                </Text>
              ),
            },
          ]}
        />

        {/* Secondary metrics: This Quarter with QoQ only */}
        <MetricRow
          items={[
            {
              label: 'AI Fluency (This Quarter)',
              labelTooltip: 'Individuals who achieved AI fluency this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.aiFluency ?? '—'}
                </Text>
              ),
              sublabel: qoqLabel(aiFluencyQoq),
            },
            {
              label: 'Digital Literacy (This Quarter)',
              labelTooltip: 'Individuals achieving digital literacy this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.digitalLiteracy ?? '—'}
                </Text>
              ),
              sublabel: qoqLabel(digitalLiteracyQoq),
            },
            {
              label: 'Digital Exposure (This Quarter)',
              labelTooltip: 'Individuals achieving digital exposure this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.digitalExposure ?? '—'}
                </Text>
              ),
              sublabel: qoqLabel(digitalExposureQoq),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Quarterly AI Fluent or Certified
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis
                dataKey="quarter"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, yAxisMax]}
                ticks={yAxisTicks}
                interval={0}
              />
              {countYAxisReferenceGridLines(yAxisTicks, gridLineStroke, chartGoalValues)}
              {showGoalLine && combinedRange ? (
                <ReferenceLine
                  y={combinedRange.min}
                  stroke={CHART_GOAL_LINE_COLOR}
                  strokeWidth={CHART_GOAL_LINE_PROPS.strokeWidth}
                  strokeDasharray={CHART_GOAL_LINE_PROPS.strokeDasharray}
                  ifOverflow="extendDomain"
                />
              ) : null}
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  if (!p) return null;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2, marginBottom: 4 }}>
                        {p.combined} AI Fluent or Certified
                      </div>
                      {showGoalLine && combinedRange ? (
                        <div style={{ fontSize: 12, opacity: 0.9 }}>
                          Goal: {combinedRange.min}–{combinedRange.max} / Quarter
                        </div>
                      ) : null}
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="combined"
                name="AI Fluent or Certified"
                fill={theme.colors.blue[6]}
                  barSize={18}
                radius={[6, 6, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
